import { createRealtimeSession } from '../utils/agentApiUtils';

// GA Realtime API: browser POSTs its SDP offer to /realtime/calls using the
// ephemeral token. The model is bound to the token server-side, so no query.
const OPENAI_REALTIME_CALLS_URL = 'https://api.openai.com/v1/realtime/calls';

/** Wait for session.created before greeting; fallback if that event never arrives. */
const GREETING_FALLBACK_MS = 3000;
/** Recover to listening if a greeting response never produces audio. */
const GREETING_RESPONSE_TIMEOUT_MS = 20000;

/** Instructions for Spacey's one-time spoken hello when voice mode connects. */
const buildVoiceGreetingInstructions = (userContext = {}) => {
  const name = (userContext.userName || '').trim().split(/\s+/)[0];
  const nameHint = name
    ? `Address them as ${name}. `
    : '';
  return (
    `${nameHint}Greet the user now in exactly two short spoken sentences. `
    + 'You are Spacey from MillionSpaces — warm, friendly, and a little playful, like a natural voice chat. '
    + 'Say hi, mention you help find workspaces in Sri Lanka, and ask what they are looking for. '
    + 'Sound relaxed and human, not scripted. Do not call any tools.'
  );
};

const responseHasAudioOutput = (response) => {
  if (!Array.isArray(response?.output)) return false;
  return response.output.some((item) => {
    if (item?.type !== 'message' || !Array.isArray(item.content)) return false;
    return item.content.some((part) => String(part?.type || '').includes('audio'));
  });
};

/**
 * RealtimeVoiceClient — Phase 0
 *
 * Manages a live, hands-free voice conversation with OpenAI's Realtime API
 * over WebRTC. Audio flows directly browser <-> OpenAI (lowest latency); our
 * backend only mints the short-lived ephemeral session token.
 *
 * Phase 0 scope: plain spoken conversation (no tool calling yet). Tool calls
 * (search_available_spaces, etc.) are wired in Phase 1.
 *
 * Lifecycle callbacks (all optional):
 *   onStatus(status)  - 'connecting' | 'live' | 'closed' | 'error'
 *   onPhase(phase)    - 'listening' | 'thinking' | 'searching' | 'speaking'
 *   onError(error)
 *   onEvent(event)    - raw realtime data-channel events (debug / future use)
 *   executeToolCall(name, args) => Promise<result> - runs a tool call on the
 *       backend; `result.modelOutput` is fed back to the model, the rest is UI.
 *   onToolResult(result, {name, args}) - lets the UI render cards/actions.
 *   onCaption({ role, text, final }) - live transcript for user/assistant.
 *   onLevel(level) - 0..1 audio amplitude for the reactive orb.
 */
export default class RealtimeVoiceClient {
  constructor({
    onStatus, onPhase, onError, onEvent, executeToolCall, onToolResult, onCaption, onLevel,
  } = {}) {
    this.onStatus = onStatus || (() => {});
    this.onPhase = onPhase || (() => {});
    this.onError = onError || (() => {});
    this.onEvent = onEvent || (() => {});
    this.executeToolCall = executeToolCall || null;
    this.onToolResult = onToolResult || (() => {});
    this.onCaption = onCaption || (() => {});
    this.onLevel = onLevel || (() => {});

    this.pc = null;
    this.dc = null;
    this.micStream = null;
    this.audioEl = null;
    this.closed = false;
    this.handledCallIds = new Set();
    this.assistantTranscript = '';
    this.phase = 'listening';
    this.userMuted = false;
    this.userContext = {};
    this.greetingSent = false;
    this.pendingGreetingResponse = false;
    this.greetingAudioHeard = false;
    this.awaitingConnectGreeting = false;
    this.greetingFallbackTimer = null;
    this.greetingResponseTimer = null;
    this.activeResponseId = null;

    this.audioCtx = null;
    this.micAnalyser = null;
    this.remoteAnalyser = null;
    this.levelRaf = null;
  }

  static isSupported() {
    return (
      typeof window !== 'undefined'
      && typeof window.RTCPeerConnection === 'function'
      && !!navigator?.mediaDevices?.getUserMedia
    );
  }

  async start(userContext = {}, voice) {
    if (!RealtimeVoiceClient.isSupported()) {
      const err = new Error('Live voice mode is not supported in this browser.');
      this.onError(err);
      this.onStatus('error');
      throw err;
    }

    this.userContext = userContext || {};
    this.greetingSent = false;
    this.pendingGreetingResponse = false;
    this.greetingAudioHeard = false;
    this.awaitingConnectGreeting = true;

    try {
      this.onStatus('connecting');
      this.setPhase('speaking');

      // 1. Mint an ephemeral session from our backend.
      // GA response: top-level `value` (ek_...). Older shape: client_secret.value.
      const session = await createRealtimeSession(userContext, voice);
      if (this.closed) return;
      const ephemeralKey = session?.value || session?.client_secret?.value;
      if (!ephemeralKey) {
        throw new Error('No ephemeral key returned from realtime-session.');
      }

      // 2. Peer connection + remote audio sink.
      const pc = new RTCPeerConnection();
      this.pc = pc;

      this.audioEl = document.createElement('audio');
      this.audioEl.autoplay = true;
      pc.ontrack = (event) => {
        if (this.audioEl && event.streams && event.streams[0]) {
          this.audioEl.srcObject = event.streams[0];
          this.attachRemoteAnalyser(event.streams[0]);
        }
      };

      pc.onconnectionstatechange = () => {
        if (this.closed) return;
        const state = pc.connectionState;
        if (state === 'connected') {
          this.onStatus('live');
          // Stay in speaking until the connect greeting finishes — not listening.
          if (this.awaitingConnectGreeting) {
            this.setPhase('speaking');
          } else {
            this.setPhase('listening');
          }
        } else if (state === 'failed' || state === 'disconnected') {
          this.onStatus('error');
        }
      };

      // 3. Microphone.
      this.micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (this.closed) { this.releaseResources(); return; }
      this.micStream.getTracks().forEach((track) => pc.addTrack(track, this.micStream));
      this.attachMicAnalyser(this.micStream);
      this.startLevelMetering();

      // 4. Data channel for realtime events.
      const dc = pc.createDataChannel('oai-events');
      this.dc = dc;
      dc.addEventListener('message', (e) => this.handleServerEvent(e));

      // 5. SDP offer/answer handshake with OpenAI.
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      if (this.closed) { this.releaseResources(); return; }

      const sdpResponse = await fetch(OPENAI_REALTIME_CALLS_URL, {
        method: 'POST',
        body: offer.sdp,
        headers: {
          Authorization: `Bearer ${ephemeralKey}`,
          'Content-Type': 'application/sdp',
        },
      });

      if (!sdpResponse.ok) {
        throw new Error(`Realtime SDP exchange failed: ${sdpResponse.status}`);
      }

      const answerSdp = await sdpResponse.text();
      if (this.closed) { this.releaseResources(); return; }
      await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });
      this.scheduleGreetingFallback();
    } catch (error) {
      // Surface the real error instead of masking it as "Ended".
      this.releaseResources();
      this.onError(error);
      this.onStatus('error');
      throw error;
    }
  }

  handleServerEvent(messageEvent) {
    let event;
    try {
      event = JSON.parse(messageEvent.data);
    } catch {
      return;
    }
    this.onEvent(event);

    // Map realtime events to orb phases.
    switch (event.type) {
      case 'session.created':
      case 'session.updated':
        this.clearGreetingFallbackTimer();
        this.sendVoiceGreeting();
        break;
      case 'error':
        // eslint-disable-next-line no-console
        console.warn('[voice] realtime error:', event);
        if (this.pendingGreetingResponse) {
          this.clearGreetingResponseTimer();
          this.pendingGreetingResponse = false;
          this.greetingAudioHeard = false;
          // Allow one retry if the greeting request was rejected (e.g. bad params).
          this.greetingSent = false;
        }
        if (this.awaitingConnectGreeting) {
          this.finishConnectGreeting();
        } else if (this.phase === 'thinking') {
          this.setPhase('listening');
        }
        break;
      case 'input_audio_buffer.speech_started':
        if (this.phase === 'speaking') {
          this.handleUserBargeIn();
        }
        break;
      case 'input_audio_buffer.speech_stopped':
        if (!this.pendingGreetingResponse) {
          this.setPhase('thinking');
        }
        break;
      case 'response.created':
        if (event.response?.id) {
          this.activeResponseId = event.response.id;
        }
        if (!this.pendingGreetingResponse) {
          this.setPhase('thinking');
        }
        break;
      // GA + legacy audio output events
      case 'response.audio.delta':
      case 'response.output_audio.delta':
      case 'response.output_audio.done':
        if (this.pendingGreetingResponse) {
          this.greetingAudioHeard = true;
          this.clearGreetingResponseTimer();
        }
        this.setPhase('speaking');
        break;
      case 'output_audio_buffer.started':
        if (this.pendingGreetingResponse) {
          this.greetingAudioHeard = true;
          this.clearGreetingResponseTimer();
        }
        this.resumeAssistantPlayback();
        this.setPhase('speaking');
        break;
      // GA + legacy assistant transcript events
      case 'response.audio_transcript.delta':
      case 'response.output_audio_transcript.delta':
        if (this.pendingGreetingResponse) {
          this.greetingAudioHeard = true;
          this.clearGreetingResponseTimer();
        }
        if (typeof event.delta === 'string') {
          this.assistantTranscript += event.delta;
          this.onCaption({ role: 'assistant', text: this.assistantTranscript, final: false });
        }
        break;
      case 'response.audio_transcript.done':
      case 'response.output_audio_transcript.done': {
        const finalText = event.transcript || this.assistantTranscript;
        if (finalText) this.onCaption({ role: 'assistant', text: finalText, final: true });
        this.assistantTranscript = '';
        break;
      }
      case 'conversation.item.input_audio_transcription.delta':
        if (typeof event.delta === 'string' && event.delta) {
          this.onCaption({ role: 'user', text: event.delta, final: false });
        }
        break;
      case 'conversation.item.input_audio_transcription.completed':
        if (event.transcript) {
          this.onCaption({ role: 'user', text: event.transcript, final: true });
        }
        break;
      // Function-call events (GA emits these in addition to response.done)
      case 'response.function_call_arguments.done':
        this.runToolCall({
          name: event.name,
          call_id: event.call_id,
          arguments: event.arguments,
        });
        break;
      case 'response.output_item.done':
        if (event.item?.type === 'function_call') {
          this.runToolCall(event.item);
        }
        break;
      case 'response.done': {
        const response = event.response;
        this.activeResponseId = null;
        const hasToolCall = Array.isArray(response?.output)
          && response.output.some((item) => item?.type === 'function_call');
        if (this.pendingGreetingResponse) {
          if (hasToolCall) {
            this.pendingGreetingResponse = false;
            this.clearGreetingResponseTimer();
          } else if (response?.status === 'failed' || response?.status === 'cancelled') {
            this.recoverFromIncompleteGreeting();
          } else if (responseHasAudioOutput(response) || this.greetingAudioHeard) {
            // Generation finished — playback may still be running; wait for
            // output_audio_buffer.stopped before returning to listening.
            this.greetingAudioHeard = true;
            this.clearGreetingResponseTimer();
          }
          // else: response.done arrived before audio events — keep waiting for
          // audio/transcript events or the greeting response timeout.
        } else if (
          (response?.status === 'failed' || response?.status === 'cancelled')
          && this.phase === 'thinking'
        ) {
          this.setPhase('listening');
        }
        this.handleFunctionCalls(response);
        break;
      }
      case 'output_audio_buffer.stopped':
        if (this.pendingGreetingResponse) {
          this.pendingGreetingResponse = false;
          this.greetingAudioHeard = false;
          this.clearGreetingResponseTimer();
          this.finishConnectGreeting();
        } else {
          this.setPhase('listening');
        }
        break;
      default:
        break;
    }
  }

  // ─── Audio-level metering (reactive orb) ───────────────────────────────────
  ensureAudioContext() {
    if (!this.audioCtx) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) this.audioCtx = new Ctx();
    }
    return this.audioCtx;
  }

  attachMicAnalyser(stream) {
    try {
      const ctx = this.ensureAudioContext();
      if (!ctx) return;
      const source = ctx.createMediaStreamSource(stream);
      this.micAnalyser = ctx.createAnalyser();
      this.micAnalyser.fftSize = 256;
      source.connect(this.micAnalyser);
    } catch { /* metering is best-effort */ }
  }

  attachRemoteAnalyser(stream) {
    try {
      const ctx = this.ensureAudioContext();
      if (!ctx) return;
      const source = ctx.createMediaStreamSource(stream);
      this.remoteAnalyser = ctx.createAnalyser();
      this.remoteAnalyser.fftSize = 256;
      source.connect(this.remoteAnalyser);
    } catch { /* metering is best-effort */ }
  }

  static rms(analyser) {
    if (!analyser) return 0;
    const buf = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i += 1) {
      const v = (buf[i] - 128) / 128;
      sum += v * v;
    }
    return Math.sqrt(sum / buf.length);
  }

  startLevelMetering() {
    const tick = () => {
      if (this.closed) return;
      const level = Math.max(
        RealtimeVoiceClient.rms(this.micAnalyser),
        RealtimeVoiceClient.rms(this.remoteAnalyser),
      );
      this.onLevel(Math.min(1, level * 2.2));
      this.levelRaf = window.requestAnimationFrame(tick);
    };
    this.levelRaf = window.requestAnimationFrame(tick);
  }

  // Sends a JSON event over the realtime data channel.
  sendEvent(event) {
    if (this.dc && this.dc.readyState === 'open') {
      this.dc.send(JSON.stringify(event));
    }
  }

  /** One-time spoken hello — only after OpenAI signals the session is ready. */
  sendVoiceGreeting() {
    if (this.greetingSent || this.closed) return;
    if (!this.dc || this.dc.readyState !== 'open') return;
    this.greetingSent = true;
    this.pendingGreetingResponse = true;
    this.greetingAudioHeard = false;
    this.setPhase('speaking');
    this.sendEvent({
      type: 'response.create',
      response: {
        // GA Realtime: only ['audio'] or ['text'] — not both.
        output_modalities: ['audio'],
        instructions: buildVoiceGreetingInstructions(this.userContext),
      },
    });
    this.scheduleGreetingResponseTimeout();
  }

  /** Connect greeting done (or failed) — open the mic for the user's turn. */
  finishConnectGreeting() {
    this.awaitingConnectGreeting = false;
    this.setPhase('listening');
  }

  scheduleGreetingFallback() {
    this.clearGreetingFallbackTimer();
    this.greetingFallbackTimer = setTimeout(() => {
      this.greetingFallbackTimer = null;
      if (!this.closed && !this.greetingSent && this.dc?.readyState === 'open') {
        this.sendVoiceGreeting();
      }
    }, GREETING_FALLBACK_MS);
  }

  scheduleGreetingResponseTimeout() {
    this.clearGreetingResponseTimer();
    this.greetingResponseTimer = setTimeout(() => {
      this.greetingResponseTimer = null;
      if (!this.closed && this.pendingGreetingResponse && !this.greetingAudioHeard) {
        this.recoverFromIncompleteGreeting();
      }
    }, GREETING_RESPONSE_TIMEOUT_MS);
  }

  recoverFromIncompleteGreeting() {
    this.pendingGreetingResponse = false;
    this.greetingAudioHeard = false;
    this.clearGreetingResponseTimer();
    if (this.awaitingConnectGreeting) {
      this.finishConnectGreeting();
    } else if (this.phase === 'thinking' || this.phase === 'speaking') {
      this.setPhase('listening');
    }
  }

  clearGreetingFallbackTimer() {
    if (this.greetingFallbackTimer) {
      clearTimeout(this.greetingFallbackTimer);
      this.greetingFallbackTimer = null;
    }
  }

  clearGreetingResponseTimer() {
    if (this.greetingResponseTimer) {
      clearTimeout(this.greetingResponseTimer);
      this.greetingResponseTimer = null;
    }
  }

  clearGreetingTimers() {
    this.clearGreetingFallbackTimer();
    this.clearGreetingResponseTimer();
  }

  // Detects function_call items in a completed response and runs them.
  handleFunctionCalls(response) {
    const output = response && Array.isArray(response.output) ? response.output : [];
    output
      .filter((item) => item && item.type === 'function_call')
      .forEach((item) => this.runToolCall(item));
  }

  async runToolCall(item) {
    const callId = item?.call_id;
    const toolName = item?.name;
    if (!callId || !toolName || this.handledCallIds.has(callId)) return;
    this.handledCallIds.add(callId);

    let args = {};
    try {
      args = item.arguments ? JSON.parse(item.arguments) : {};
    } catch {
      args = {};
    }

    // eslint-disable-next-line no-console
    console.log('[voice] function call:', toolName, args);

    let modelOutput = { error: 'Tool unavailable' };
    try {
      this.setPhase('searching');
      if (this.executeToolCall) {
        const result = await this.executeToolCall(toolName, args);
        if (this.closed) return;
        modelOutput = result?.modelOutput ?? {};
        // eslint-disable-next-line no-console
        console.log('[voice] tool exec result:', toolName, {
          spaces: result?.spaces?.length ?? 0,
          found: result?.modelOutput?.found,
        });
        this.onToolResult(result || {}, { name: toolName, args });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[voice] tool exec failed:', toolName, error);
      modelOutput = { error: error?.message || 'Tool execution failed' };
    }

    // Return the tool result to the model, then ask it to respond.
    this.sendEvent({
      type: 'conversation.item.create',
      item: {
        type: 'function_call_output',
        call_id: callId,
        output: JSON.stringify(modelOutput),
      },
    });
    this.sendEvent({ type: 'response.create' });
    this.setPhase('thinking');
  }

  /** User spoke over Spacey — stop playback, cancel response, start a new turn. */
  handleUserBargeIn() {
    this.stopAssistantPlayback();
    this.sendEvent({ type: 'response.cancel' });
    this.assistantTranscript = '';
    if (this.pendingGreetingResponse) {
      this.pendingGreetingResponse = false;
      this.greetingAudioHeard = false;
      this.clearGreetingResponseTimer();
      if (this.awaitingConnectGreeting) {
        this.awaitingConnectGreeting = false;
      }
    }
    this.activeResponseId = null;
    this.setPhase('listening');
  }

  stopAssistantPlayback() {
    if (this.audioEl) {
      try {
        this.audioEl.pause();
      } catch { /* noop */ }
    }
    this.sendEvent({ type: 'output_audio_buffer.clear' });
  }

  resumeAssistantPlayback() {
    if (this.audioEl) {
      try {
        const playPromise = this.audioEl.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
      } catch { /* noop */ }
    }
  }

  /** Updates orb phase and gates the mic — open while listening or while Spacey speaks (barge-in). */
  setPhase(phase) {
    if (this.closed) return;
    this.phase = phase;
    this.onPhase(phase);
    this.applyMicGate();
  }

  applyMicGate() {
    const micOpen = !this.userMuted
      && (this.phase === 'listening' || this.phase === 'speaking');
    this.setMicTracksEnabled(micOpen);
  }

  setMicTracksEnabled(enabled) {
    if (!this.micStream) return;
    this.micStream.getAudioTracks().forEach((track) => {
      track.enabled = enabled;
    });
  }

  setMuted(muted) {
    this.userMuted = muted;
    this.applyMicGate();
  }

  // Releases media/network resources without emitting a status change.
  releaseResources() {
    this.closed = true;
    this.clearGreetingTimers();
    this.pendingGreetingResponse = false;
    this.greetingAudioHeard = false;
    this.awaitingConnectGreeting = false;
    if (this.levelRaf) {
      try { window.cancelAnimationFrame(this.levelRaf); } catch { /* noop */ }
      this.levelRaf = null;
    }
    try {
      if (this.audioCtx) this.audioCtx.close();
    } catch { /* noop */ }
    this.audioCtx = null;
    this.micAnalyser = null;
    this.remoteAnalyser = null;
    try {
      if (this.dc) this.dc.close();
    } catch { /* noop */ }
    try {
      if (this.micStream) this.micStream.getTracks().forEach((t) => t.stop());
    } catch { /* noop */ }
    try {
      if (this.pc) this.pc.close();
    } catch { /* noop */ }
    if (this.audioEl) {
      this.audioEl.srcObject = null;
      this.audioEl = null;
    }
    this.dc = null;
    this.pc = null;
    this.micStream = null;
  }

  // User-initiated end of the session.
  stop() {
    this.releaseResources();
    this.onStatus('closed');
  }
}
